import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserRole } from '../enums/global.enum';
import { UserApi } from '@shared-features/services';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums/shipments.enum';
import { RolesConstants } from '@shared/constants';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { ShipmentsListType } from '@shared-features/enums';

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  constructor(private userApi: UserApi) {}

  /**
   * Reactive variant of {@link isUserHasRoles}. When a session exists (valid OR
   * expired token) but `currentUser$` hasn't been hydrated yet — e.g. on a fresh
   * tab while the token is being restored/refreshed — it waits for the user to be
   * populated before evaluating, so callers (route guards) don't wrongly deny an
   * already-authenticated user. Falls back to a synchronous decision when the
   * user is already available or there is no session at all.
   */
  isUserHasRoles$(roles: UserRole[]): Observable<boolean> {
    if (this.userApi.currentUser$.value) {
      return of(this.isUserHasRoles(roles));
    }
    return this.userApi.currentUser$.pipe(
      filter((user) => !!user),
      take(1),
      map(() => this.isUserHasRoles(roles))
    );
  }

  isUserHasRoles(roles: UserRole[]): boolean {
    if (!roles || !roles?.length) return false;
    for (const role of roles) {
      const userRole = (this.userApi.currentUser$.value?.userRoles || []).find((item) => item === role);
      if (userRole) {
        return true;
      }
    }
    return false;
  }

  /**
   * The first route the current user's role is actually allowed to land on.
   *
   * This is the single source of truth for "where is home for this user". It is
   * reused wherever the app picks a destination on the user's behalf rather than
   * at their explicit request — the login landing branch, and the role-aware
   * home redirect — so a role that cannot see the dashboard is never
   * dropped on /access-denied by the app's own default routing.
   */
  landingRoute(): string {
    if (this.isUserHasRoles(RolesConstants.DASHBOARD_ORGANIZATION_VIEW)) {
      return RoutesUtil.OrganizationDashboard.url();
    }
    return this.isUserHasRoles(RolesConstants.DASHBOARD_VIEW)
      ? RoutesUtil.Dashboards.url()
      : RoutesUtil.ShipmentsList.url({ type: ShipmentsListType.Outbound });
  }

  hasBiddingTemplate(): boolean {
    return (this.userApi.currentUser$.value?.creationTemplates || []).some(
      (template) => template.type === ShipmentCreationTemplateTypes.Bidding
    );
  }
}
